import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useGetComplianceCaseQuery } from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery, type ProfileType } from "~/graphql/supergraphTypes";
import { extractChildProfiles } from "../../../Documents/utils";
import { FlattenedProfileArrayType, ProfileWithScreening } from "../../types";
import { ViewScreeningDetailsFullscreenTakeover } from "../index";
import { useScreeningPolling } from "./useScreeningPolling";

interface WrapperProps {
  caseId: number;
  complianceCase: GetComplianceCaseQuery["complianceCase"] | undefined;
  readonly: boolean;
}

export const ScreeningWrapper = ({ caseId, complianceCase, readonly }: WrapperProps) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get("profileId") || "";
  const caseStatus = complianceCase?.status;

  const screeningRecordsResultObject = useScreeningPolling({
    caseId,
    programId: complianceCase?.programId,
    profileId,
  });

  const profileDetailsResults = useGetComplianceCaseQuery({
    variables: {
      caseId: caseId,
    },
    fetchPolicy: "no-cache",
  });

  const fullComplianceData = useMemo(() => {
    if (!complianceCase || !screeningRecordsResultObject.data?.complianceCase) {
      return complianceCase;
    }

    const screeningCase = screeningRecordsResultObject.data.complianceCase;

    const mergedData = {
      ...complianceCase,
      investingProfile: complianceCase.investingProfile && {
        ...complianceCase.investingProfile,
        screeningRecord: screeningCase.investingProfile?.screeningRecord ?? null,
        screeningMatches: screeningCase.investingProfile?.screeningMatches ?? [],
      },
      profiles: complianceCase.profiles.map((profile) => {
        if (!profile) return profile;
        const matchingProfile = screeningCase.profiles?.find((p) => p?.id === profile.id);
        return {
          ...profile,
          screeningRecord: matchingProfile?.screeningRecord ?? null,
          screeningMatches: matchingProfile?.screeningMatches ?? [],
        };
      }),
    };

    return mergedData;
  }, [complianceCase, screeningRecordsResultObject.data]);

  const flattenedProfiles: FlattenedProfileArrayType = extractChildProfiles(fullComplianceData);
  const currentSelectedProfile = flattenedProfiles.find(
    (profile) => profile?.id === parseInt(profileId, 10),
  ) as ProfileWithScreening | undefined;

  // recurringHitRootProfile is only populated for recurring_hit cases; it resolves the root
  // investor account so an analyst can jump from the screened owner/controller to the account.
  const recurringHitRootProfile = complianceCase?.recurringHitRootProfile ?? null;

  const onExit = () => {
    history.goBack();
  };

  if (!profileId) {
    onExit();
  }

  return (
    <ViewScreeningDetailsFullscreenTakeover
      readonly={readonly}
      currentSelectedProfile={currentSelectedProfile}
      investingProfile={
        profileDetailsResults.data?.complianceCase?.investingProfile as ProfileType | undefined
      }
      recurringHitRootProfile={recurringHitRootProfile}
      onExit={onExit}
      queryResults={[profileDetailsResults, screeningRecordsResultObject]}
      isPolling={screeningRecordsResultObject.isPolling}
      riskAssessmentFailed={screeningRecordsResultObject.riskAssessmentFailed}
      caseStatus={caseStatus}
    />
  );
};
